import {
  UsersThree,
  CalendarBlank,
  CreditCard,
  VideoCamera,
  Sparkle,
  HandPalm,
  type Icon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { accentAt } from "./palette";

const BENEFITS_ICONS: Icon[] = [
  UsersThree,
  CalendarBlank,
  CreditCard,
  VideoCamera,
  Sparkle,
  HandPalm,
];

// The two mirrored blob shapes from the home page's therapist photos, so the
// icon tiles here are cut from the same hand as the portraits there.
const BLOB_A = "46% 54% 57% 43% / 52% 46% 54% 48%";
const BLOB_B = "54% 46% 43% 57% / 46% 52% 48% 54%";

export function Benefits() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative py-16 sm:py-24"
      style={{ background: "var(--color-ther-linen)" }}
    >
      <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20 lg:px-8">
        {/* Copy stays put while the list scrolls past it */}
        <div className="text-center lg:sticky lg:top-28 lg:self-start lg:text-left">
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            {t("forTherapists.benefits.title")}
          </h2>
          <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
            {t("forTherapists.benefits.intro")}
          </p>
        </div>

        <ul className="grid gap-x-8 gap-y-10 sm:grid-cols-2">
          {BENEFITS_ICONS.map((IconCmp, i) => {
            const accent = accentAt(i);
            return (
              <li key={i} className="flex flex-col items-center text-center sm:items-start sm:text-left">
                <span
                  className="flex size-16 items-center justify-center"
                  style={{
                    background: accent.bg,
                    borderRadius: i % 2 === 0 ? BLOB_A : BLOB_B,
                  }}
                >
                  <IconCmp className="size-7" style={{ color: accent.fg }} weight="regular" aria-hidden />
                </span>
                <h3 className="font-heading mt-5 text-xl font-bold leading-[1.2] tracking-[-0.01em] text-foreground">
                  {t(`forTherapists.benefits.item${i + 1}Title`)}
                </h3>
                <p className="mt-2.5 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
                  {t(`forTherapists.benefits.item${i + 1}Desc`)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
